import type React from "react";
import { useMemo, useState } from "react";
import { Button } from "../../../lib/components/ui/button";
import { Input } from "../../../lib/components/ui/input";
import { Label } from "../../../lib/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../lib/components/ui/select";
import { Switch } from "../../../lib/components/ui/switch";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../../lib/components/ui/tabs";
import { CATEGORY_EXTRAS_MAP } from "../../../lib/constants";
import type { GetTypes } from "../../../lib/types/api-types";

interface MenuExtraFormProps {
	initialData?: GetTypes["menu"]["getExtras"] extends { extras?: infer Extras }
		? Extras extends (infer Extra)[]
			? Extra
			: never
		: never;
	onSubmit: (formData: FormData) => void;
}

export function MenuExtraForm({ initialData, onSubmit }: MenuExtraFormProps) {
	// Parse initial pricing rules
	const initialRules = useMemo(() => {
		try {
			return JSON.parse(initialData?.pricingRules || "{}");
		} catch (e) {
			return {};
		}
	}, [initialData]);

	const initialMetadata = useMemo(() => {
		try {
			return JSON.parse(initialData?.metadata || "{}");
		} catch (e) {
			return {};
		}
	}, [initialData]);

	const [pricingType, setPricingType] = useState<"fixed" | "sizeBased">(
		initialRules.sizeBased ? "sizeBased" : "fixed",
	);

	// Get unique categories from constants
	const uniqueCategories = useMemo(() => {
		const categories = new Set<string>();
		Object.values(CATEGORY_EXTRAS_MAP).forEach((arr) => {
			arr.forEach((c) => categories.add(c));
		});
		return Array.from(categories).sort();
	}, []);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		onSubmit(formData);
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div className="grid grid-cols-2 gap-4">
				<div className="col-span-2 sm:col-span-1">
					<Label htmlFor="name">Name *</Label>
					<Input
						id="name"
						name="name"
						defaultValue={initialData?.name || ""}
						required
						className="mt-1"
						placeholder="e.g. Extra Cheese"
					/>
				</div>
				<div className="col-span-2 sm:col-span-1">
					<Label htmlFor="type">Type *</Label>
					<Select
						name="type"
						defaultValue={initialData?.type || "topping"}
						required
					>
						<SelectTrigger className="mt-1">
							<SelectValue placeholder="Select type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="topping">Topping</SelectItem>
							<SelectItem value="sauce">Sauce</SelectItem>
							<SelectItem value="side">Side</SelectItem>
							<SelectItem value="dressing">Dressing</SelectItem>
							<SelectItem value="dip">Dip</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<div>
				<Label htmlFor="category">Category *</Label>
				<Select
					name="category"
					defaultValue={initialData?.category || ""}
					required
				>
					<SelectTrigger className="mt-1">
						<SelectValue placeholder="Select category code" />
					</SelectTrigger>
					<SelectContent>
						{uniqueCategories.map((cat) => (
							<SelectItem key={cat} value={cat}>
								{cat}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<p className="text-xs text-muted-foreground mt-1">
					Links this extra to specific menu categories (e.g. stufe_a for basic
					pizza toppings)
				</p>
			</div>

			<div className="space-y-4 border rounded-lg p-4 bg-muted/10">
				<div className="flex items-center justify-between">
					<Label className="text-base font-medium">Pricing Configuration</Label>
					<Tabs
						value={pricingType}
						onValueChange={(v) => setPricingType(v as any)}
						className="w-[200px]"
					>
						<TabsList className="grid w-full grid-cols-2 h-8">
							<TabsTrigger value="fixed" className="text-xs">
								Fixed
							</TabsTrigger>
							<TabsTrigger value="sizeBased" className="text-xs">
								Size Based
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
				<input type="hidden" name="pricingType" value={pricingType} />

				<div className="pt-2">
					{pricingType === "fixed" ? (
						<div className="max-w-[200px]">
							<Label htmlFor="fixedPrice">Price (€)</Label>
							<div className="relative mt-1">
								<span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
									€
								</span>
								<Input
									id="fixedPrice"
									name="fixedPrice"
									type="number"
									step="0.01"
									min="0"
									defaultValue={initialRules.fixedPrice || ""}
									className="pl-7"
									required
								/>
							</div>
						</div>
					) : (
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
							{["klein", "normal", "gross", "pan-pizza", "blech"].map(
								(size) => (
									<div key={size}>
										<Label
											htmlFor={`price_${size}`}
											className="capitalize text-xs"
										>
											{size} (€)
										</Label>
										<Input
											id={`price_${size}`}
											name={`price_${size}`}
											type="number"
											step="0.01"
											min="0"
											placeholder="0.00"
											defaultValue={initialRules.sizeBased?.[size] || ""}
											className="mt-1"
										/>
									</div>
								),
							)}
						</div>
					)}
				</div>
			</div>

			<div className="space-y-4 border rounded-lg p-4 bg-muted/10">
				<Label className="text-base font-medium">Dietary & Availability</Label>

				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
					<div className="flex items-center space-x-2">
						<Switch
							id="isVegan"
							name="isVegan"
							defaultChecked={initialMetadata.isVegan || false}
						/>
						<Label htmlFor="isVegan" className="font-normal">
							Vegan
						</Label>
					</div>

					<div className="flex items-center space-x-2">
						<Switch
							id="isVegetarian"
							name="isVegetarian"
							defaultChecked={initialMetadata.isVegetarian || false}
						/>
						<Label htmlFor="isVegetarian" className="font-normal">
							Vegetarian
						</Label>
					</div>

					<div className="flex items-center space-x-2">
						<Switch
							id="isAvailable"
							name="isAvailable"
							defaultChecked={initialData?.isAvailable ?? true}
						/>
						<Label htmlFor="isAvailable" className="font-normal">
							Available
						</Label>
					</div>
				</div>
			</div>

			<div className="flex justify-end gap-3 pt-4 border-t">
				<Button type="submit">
					{initialData ? "Update Extra" : "Create Extra"}
				</Button>
			</div>
		</form>
	);
}
